import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import { cn } from "../lib/cn";
import { MenuRowIcon, menuRowClassName, renderMenuRowChildren, type MenuRowOptions, type MenuRowVariant } from "../lib/menu-row";

// Sibling-aware submenu close
//
// Radix's `SubTrigger` uses a "safe-intent" grace window: when the pointer
// leaves the trigger heading toward the submenu, the submenu stays open for
// a beat so the user can travel into it without it snapping shut. The cost
// is that moving the pointer to a sibling item leaves the submenu visibly
// open for a moment, even though the user is clearly heading away. In a
// menu with multiple items + submenus, this reads as a janky "two things
// look active at once".
// Each content level provides a registry of "close me" callbacks; hovering an
// item closes sibling submenus at that level only (a sub-trigger exempts its
// own), so consumers never wire `onPointerEnter` + controlled state themselves.

type SubMenuRegistry = {
  register: (id: string, close: () => void) => () => void;
  closeAll: (exceptId?: string) => void;
};
const SubMenuRegistryContext = React.createContext<SubMenuRegistry | null>(null);

// Each DropdownMenuSub provides its own id to its descendants so the
// SubTrigger can ask the parent registry to "close everyone except me".
const SubMenuIdContext = React.createContext<string | null>(null);
function useFreshSubMenuRegistry() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const subMenusRef = React.useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      register: (id, close) => {
        subMenusRef.current.set(id, close);
        return () => {
          subMenusRef.current.delete(id);
        };
      },
      closeAll: exceptId => {
        subMenusRef.current.forEach((close_0, id_0) => {
          if (id_0 !== exceptId) {
            close_0();
          }
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

// Compose two event handlers without dropping the consumer's handler. If the
// consumer's handler calls preventDefault we still run ours, since closing a
// stale submenu shouldn't be cancellable from a sibling.
function composeEventHandlers<E extends React.SyntheticEvent>(ours: (event: E) => void, theirs: ((event: E) => void) | undefined) {
  return (event: E) => {
    theirs?.(event);
    ours(event);
  };
}
const DropdownMenuSub = t0 => {
  const $ = _c(28);
  let children;
  let controlledOpen;
  let defaultOpen;
  let onOpenChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      open: controlledOpen,
      defaultOpen,
      onOpenChange,
      closeOnSiblingHover: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = controlledOpen;
    $[3] = defaultOpen;
    $[4] = onOpenChange;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    controlledOpen = $[2];
    defaultOpen = $[3];
    onOpenChange = $[4];
    props = $[5];
    t1 = $[6];
  }
  const closeOnSiblingHover = t1 === undefined ? true : t1;
  const parentRegistry = React.useContext(SubMenuRegistryContext);
  const id = React.useId();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const onOpenChangeRef = React.useRef(onOpenChange);
  let t2;
  let t3;
  if ($[7] !== onOpenChange) {
    t2 = () => {
      onOpenChangeRef.current = onOpenChange;
    };
    t3 = [onOpenChange];
    $[7] = onOpenChange;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  React.useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== closeOnSiblingHover || $[11] !== id || $[12] !== isControlled || $[13] !== parentRegistry) {
    t4 = () => {
      if (!parentRegistry || !closeOnSiblingHover) {
        return;
      }
      return parentRegistry.register(id, () => {
        if (isControlled) {
          onOpenChangeRef.current?.(false);
        } else {
          setInternalOpen(false);
        }
      });
    };
    t5 = [parentRegistry, id, isControlled, closeOnSiblingHover];
    $[10] = closeOnSiblingHover;
    $[11] = id;
    $[12] = isControlled;
    $[13] = parentRegistry;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[16] !== id || $[17] !== isControlled || $[18] !== parentRegistry) {
    t6 = next => {
      if (next && parentRegistry) {
        parentRegistry.closeAll(id);
      }
      if (isControlled) {
        onOpenChangeRef.current?.(next);
      } else {
        setInternalOpen(next);
      }
    };
    $[16] = id;
    $[17] = isControlled;
    $[18] = parentRegistry;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[20] !== children || $[21] !== handleOpenChange || $[22] !== open || $[23] !== props) {
    t7 = <DropdownMenuPrimitive.Sub open={open} onOpenChange={handleOpenChange} {...props}>{children}</DropdownMenuPrimitive.Sub>;
    $[20] = children;
    $[21] = handleOpenChange;
    $[22] = open;
    $[23] = props;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== id || $[26] !== t7) {
    t8 = <SubMenuIdContext.Provider value={id}>{t7}</SubMenuIdContext.Provider>;
    $[25] = id;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
};
const DropdownMenuSubTrigger = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
  icon?: React.ReactNode;
}>((t0, ref) => {
  const $ = _c(27);
  let children;
  let className;
  let icon;
  let inset;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      icon,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = inset;
    $[5] = onPointerEnter;
    $[6] = props;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    inset = $[4];
    onPointerEnter = $[5];
    props = $[6];
  }
  const registry = React.useContext(SubMenuRegistryContext);
  const ownId = React.useContext(SubMenuIdContext);
  const t1 = inset && "pl-7";
  const t2 = icon != null && "gap-2";
  let t3;
  if ($[7] !== className || $[8] !== t1 || $[9] !== t2) {
    t3 = cn("group/menu-row flex cursor-default select-none items-center rounded-md px-2 py-[3px] type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", t1, t2, className);
    $[7] = className;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== ownId || $[12] !== registry) {
    t4 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      registry?.closeAll(ownId ?? undefined);
    };
    $[11] = ownId;
    $[12] = registry;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== onPointerEnter || $[15] !== t4) {
    t5 = composeEventHandlers(t4, onPointerEnter);
    $[14] = onPointerEnter;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== children || $[18] !== icon) {
    t6 = renderMenuRowChildren(children, {
      icon
    });
    $[17] = children;
    $[18] = icon;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronRight className="ml-auto h-4 w-4 shrink-0" />;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== props || $[22] !== ref || $[23] !== t3 || $[24] !== t5 || $[25] !== t6) {
    t8 = <DropdownMenuPrimitive.SubTrigger ref={ref} className={t3} onPointerEnter={t5} {...props}>{t6}{t7}</DropdownMenuPrimitive.SubTrigger>;
    $[21] = props;
    $[22] = ref;
    $[23] = t3;
    $[24] = t5;
    $[25] = t6;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
});
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.SubContent>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
  /** Caps the height for long lists and scrolls the overflow. */
  scrollable?: boolean;
}>((t0, ref) => {
  const $ = _c(14);
  let className;
  let props;
  let scrollable;
  if ($[0] !== t0) {
    ({
      className,
      scrollable,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = scrollable;
  } else {
    className = $[1];
    props = $[2];
    scrollable = $[3];
  }
  const registry = useFreshSubMenuRegistry();
  const t1 = scrollable && "max-h-[min(320px,50vh)] overflow-y-auto";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("z-[9999] min-w-[8rem] overflow-visible rounded-[10px] border border-[#0c0c0d14] bg-white p-1 text-neutral-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-6px_rgba(0,0,0,0.12)] dark:border-white/12 dark:bg-[#2A2C2C] dark:text-white dark:shadow-[0_1px_2px_rgba(0,0,0,0.2),0_8px_24px_-6px_rgba(0,0,0,0.4)]", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <DropdownMenuPrimitive.SubContent ref={ref} className={t2} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== registry || $[12] !== t3) {
    t4 = <DropdownMenuPrimitive.Portal><SubMenuRegistryContext.Provider value={registry}>{t3}</SubMenuRegistryContext.Provider></DropdownMenuPrimitive.Portal>;
    $[11] = registry;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
});
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  /** Portal target instead of `document.body`; a fullscreen element must host its own menus. */
  container?: HTMLElement | null;
}>((t0, ref) => {
  const $ = _c(18);
  let className;
  let container;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      container,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = container;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    container = $[2];
    props = $[3];
    t1 = $[4];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  const registry = useFreshSubMenuRegistry();
  let t2;
  if ($[5] !== className) {
    t2 = cn("z-100 min-w-[8rem] overflow-hidden rounded-[10px] border border-[#0c0c0d14] bg-white p-1 text-neutral-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-6px_rgba(0,0,0,0.12)] dark:border-white/12 dark:bg-[#2A2C2C] dark:text-white dark:shadow-[0_1px_2px_rgba(0,0,0,0.2),0_8px_24px_-6px_rgba(0,0,0,0.4)] outline-none focus:outline-none focus-visible:outline-none", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== sideOffset || $[10] !== t2) {
    t3 = <DropdownMenuPrimitive.Content ref={ref} sideOffset={sideOffset} className={t2} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = sideOffset;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== registry || $[13] !== t3) {
    t4 = <SubMenuRegistryContext.Provider value={registry}>{t3}</SubMenuRegistryContext.Provider>;
    $[12] = registry;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== container || $[16] !== t4) {
    t5 = <DropdownMenuPrimitive.Portal container={container}>{t4}</DropdownMenuPrimitive.Portal>;
    $[15] = container;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

// Shared hook: any item (regular, checkbox, radio) that is hovered should
// close any sibling submenus at the same level. Mouse pointers only — touch
// pointers don't trigger hover-style close.
function useCloseSiblingSubmenus() {
  const $ = _c(2);
  const registry = React.useContext(SubMenuRegistryContext);
  let t0;
  if ($[0] !== registry) {
    t0 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      registry?.closeAll();
    };
    $[0] = registry;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Item>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & MenuRowOptions & {
  inset?: boolean;
  variant?: MenuRowVariant;
}>((t0, ref) => {
  const $ = _c(39);
  let asChild;
  let children;
  let className;
  let description;
  let icon;
  let inset;
  let onClick;
  let onPointerEnter;
  let props;
  let t1;
  let trailing;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      icon,
      description,
      trailing,
      variant: t1,
      asChild,
      children,
      onClick,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = description;
    $[5] = icon;
    $[6] = inset;
    $[7] = onClick;
    $[8] = onPointerEnter;
    $[9] = props;
    $[10] = t1;
    $[11] = trailing;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    description = $[4];
    icon = $[5];
    inset = $[6];
    onClick = $[7];
    onPointerEnter = $[8];
    props = $[9];
    t1 = $[10];
    trailing = $[11];
  }
  const variant = t1 === undefined ? "default" : t1;
  const closeSiblings = useCloseSiblingSubmenus();
  let t2;
  if ($[12] !== onClick) {
    t2 = e => {
      onClick?.(e);
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.blur();
      }
    };
    $[12] = onClick;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const handleClick = t2;
  const t3 = inset && "pl-7";
  let t4;
  if ($[14] !== className || $[15] !== description || $[16] !== icon || $[17] !== t3 || $[18] !== trailing || $[19] !== variant) {
    t4 = cn("relative flex cursor-pointer delay-0 duration-0 select-none items-center rounded-md px-2 py-[3px] type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", t3, menuRowClassName({
      icon,
      description,
      trailing
    }, variant), className);
    $[14] = className;
    $[15] = description;
    $[16] = icon;
    $[17] = t3;
    $[18] = trailing;
    $[19] = variant;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== closeSiblings || $[22] !== onPointerEnter) {
    t5 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[21] = closeSiblings;
    $[22] = onPointerEnter;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== asChild || $[25] !== children || $[26] !== description || $[27] !== icon || $[28] !== trailing || $[29] !== variant) {
    t6 = renderMenuRowChildren(children, {
      icon,
      description,
      trailing
    }, {
      asChild,
      variant
    });
    $[24] = asChild;
    $[25] = children;
    $[26] = description;
    $[27] = icon;
    $[28] = trailing;
    $[29] = variant;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== asChild || $[32] !== handleClick || $[33] !== props || $[34] !== ref || $[35] !== t4 || $[36] !== t5 || $[37] !== t6) {
    t7 = <DropdownMenuPrimitive.Item ref={ref} className={t4} asChild={asChild} onClick={handleClick} onPointerEnter={t5} {...props}>{t6}</DropdownMenuPrimitive.Item>;
    $[31] = asChild;
    $[32] = handleClick;
    $[33] = props;
    $[34] = ref;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>>((t0, ref) => {
  const $ = _c(19);
  let checked;
  let children;
  let className;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      checked,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = onPointerEnter;
    $[5] = props;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    onPointerEnter = $[4];
    props = $[5];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[6] !== className) {
    t1 = cn("relative flex cursor-default select-none items-center rounded-md py-[3px] pl-7 pr-2 type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== closeSiblings || $[9] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[8] = closeSiblings;
    $[9] = onPointerEnter;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><DropdownMenuPrimitive.ItemIndicator><Check className="h-3.5 w-3.5" strokeWidth={2.25} /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== checked || $[13] !== children || $[14] !== props || $[15] !== ref || $[16] !== t1 || $[17] !== t2) {
    t4 = <DropdownMenuPrimitive.CheckboxItem ref={ref} className={t1} checked={checked} onPointerEnter={t2} {...props}>{t3}{children}</DropdownMenuPrimitive.CheckboxItem>;
    $[12] = checked;
    $[13] = children;
    $[14] = props;
    $[15] = ref;
    $[16] = t1;
    $[17] = t2;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
});
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

// Checkbox item with checkmark on the right (e.g. filter menus).
const DropdownMenuCheckboxItemRight = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
  icon?: React.ReactNode;
}>((t0, ref) => {
  const $ = _c(28);
  let checked;
  let children;
  let className;
  let icon;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      children,
      checked,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = icon;
    $[5] = onPointerEnter;
    $[6] = props;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    icon = $[4];
    onPointerEnter = $[5];
    props = $[6];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[7] !== className) {
    t1 = cn("group/menu-row relative flex w-full cursor-default select-none items-center justify-between gap-2 rounded-md py-[3px] pl-2 pr-2 text-left type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== closeSiblings || $[10] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[9] = closeSiblings;
    $[10] = onPointerEnter;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = icon != null && "flex items-center gap-2";
  let t4;
  if ($[12] !== t3) {
    t4 = cn("min-w-0 flex-1 text-left", t3);
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== icon) {
    t5 = icon != null && <MenuRowIcon>{icon}</MenuRowIcon>;
    $[14] = icon;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== children || $[17] !== t4 || $[18] !== t5) {
    t6 = <span className={t4}>{t5}{children}</span>;
    $[16] = children;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="flex h-4 w-4 shrink-0 items-center justify-center"><DropdownMenuPrimitive.ItemIndicator><Check className="h-3.5 w-3.5" strokeWidth={2.25} /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== checked || $[22] !== props || $[23] !== ref || $[24] !== t1 || $[25] !== t2 || $[26] !== t6) {
    t8 = <DropdownMenuPrimitive.CheckboxItem ref={ref} className={t1} checked={checked} onPointerEnter={t2} {...props}>{t6}{t7}</DropdownMenuPrimitive.CheckboxItem>;
    $[21] = checked;
    $[22] = props;
    $[23] = ref;
    $[24] = t1;
    $[25] = t2;
    $[26] = t6;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
});
DropdownMenuCheckboxItemRight.displayName = "DropdownMenuCheckboxItemRight";
const DropdownMenuRadioItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>>((t0, ref) => {
  const $ = _c(17);
  let children;
  let className;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onPointerEnter;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    onPointerEnter = $[3];
    props = $[4];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex cursor-default select-none items-center rounded-md py-[3px] pl-7 pr-2 type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== closeSiblings || $[8] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[7] = closeSiblings;
    $[8] = onPointerEnter;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><DropdownMenuPrimitive.ItemIndicator><Circle className="h-2 w-2 fill-current" /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== props || $[13] !== ref || $[14] !== t1 || $[15] !== t2) {
    t4 = <DropdownMenuPrimitive.RadioItem ref={ref} className={t1} onPointerEnter={t2} {...props}>{t3}{children}</DropdownMenuPrimitive.RadioItem>;
    $[11] = children;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuRadioItemRight = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & Pick<MenuRowOptions, "description"> & {
  /** `none` marks the selected row by weight alone, e.g. a short list of values like playback speeds. */
  indicator?: "check" | "none";
}>((t0, ref) => {
  const $ = _c(28);
  let children;
  let className;
  let description;
  let onPointerEnter;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      description,
      indicator: t1,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = description;
    $[4] = onPointerEnter;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    description = $[3];
    onPointerEnter = $[4];
    props = $[5];
    t1 = $[6];
  }
  const indicator = t1 === undefined ? "check" : t1;
  const closeSiblings = useCloseSiblingSubmenus();
  const t2 = description != null && "h-auto";
  const t3 = indicator === "none" && "data-[state=checked]:font-semibold";
  let t4;
  if ($[7] !== className || $[8] !== description || $[9] !== t2 || $[10] !== t3) {
    t4 = cn("relative flex cursor-pointer select-none items-center justify-between h-7 rounded-md px-1.5 py-[3px] type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", t2, t3, menuRowClassName({
      description
    }), className);
    $[7] = className;
    $[8] = description;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== closeSiblings || $[13] !== onPointerEnter) {
    t5 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[12] = closeSiblings;
    $[13] = onPointerEnter;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== children || $[16] !== description) {
    t6 = renderMenuRowChildren(children, {
      description
    });
    $[15] = children;
    $[16] = description;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== description || $[19] !== indicator) {
    t7 = indicator === "check" && <DropdownMenuPrimitive.ItemIndicator className={cn("ml-3 flex h-4 w-4 items-center justify-center", description != null && "h-[22px]")}><Check className="h-3.5 w-3.5" strokeWidth={2.25} /></DropdownMenuPrimitive.ItemIndicator>;
    $[18] = description;
    $[19] = indicator;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== props || $[22] !== ref || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <DropdownMenuPrimitive.RadioItem ref={ref} className={t4} onPointerEnter={t5} {...props}>{t6}{t7}</DropdownMenuPrimitive.RadioItem>;
    $[21] = props;
    $[22] = ref;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
});
DropdownMenuRadioItemRight.displayName = "DropdownMenuRadioItemRight";
const MENU_LABEL_VARIANT_CLASS = {
  section: "px-2 pt-2 pb-1 text-[11px] leading-4 font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400",
  heading: "px-2 py-1.5 type-page-title-m"
} as const;
const DropdownMenuLabel = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Label>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
  variant?: keyof typeof MENU_LABEL_VARIANT_CLASS;
}>((t0, ref) => {
  const $ = _c(13);
  let className;
  let inset;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    inset = $[2];
    props = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "section" : t1;
  const t2 = MENU_LABEL_VARIANT_CLASS[variant];
  const t3 = inset && "pl-7";
  let t4;
  if ($[5] !== className || $[6] !== t2 || $[7] !== t3) {
    t4 = cn(t2, t3, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props || $[10] !== ref || $[11] !== t4) {
    t5 = <DropdownMenuPrimitive.Label ref={ref} className={t4} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Separator>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-neutral-100 dark:bg-[#444545]", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <DropdownMenuPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

/**
 * A 28px row for inline controls (e.g. a segmented toggle). The row itself is
 * not a menu item and never highlights. Radix arrow keys only reach menu
 * items and Tab never enters a menu, so build its controls from
 * DropdownMenuControlOption.
 */
const DropdownMenuControlRow = React.forwardRef<HTMLDivElement, Omit<React.HTMLAttributes<HTMLDivElement>, "role"> & {
  icon?: React.ReactNode;
  label: React.ReactNode;
}>((t0, ref) => {
  const $ = _c(25);
  let children;
  let className;
  let icon;
  let label;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      label,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = label;
    $[5] = onPointerEnter;
    $[6] = props;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    label = $[4];
    onPointerEnter = $[5];
    props = $[6];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  const t1 = typeof label === "string" ? label : undefined;
  let t2;
  if ($[7] !== className) {
    t2 = cn("flex h-7 cursor-default select-none items-center gap-2 px-2 type-body-s-regular text-neutral-900 dark:text-white", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== closeSiblings || $[10] !== onPointerEnter) {
    t3 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[9] = closeSiblings;
    $[10] = onPointerEnter;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== icon) {
    t4 = icon != null && <MenuRowIcon>{icon}</MenuRowIcon>;
    $[12] = icon;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== label) {
    t5 = <span className="min-w-0 flex-1 truncate">{label}</span>;
    $[14] = label;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== children || $[17] !== props || $[18] !== ref || $[19] !== t1 || $[20] !== t2 || $[21] !== t3 || $[22] !== t4 || $[23] !== t5) {
    t6 = <div ref={ref} role="group" aria-label={t1} className={t2} onPointerEnter={t3} {...props}>{t4}{t5}{children}</div>;
    $[16] = children;
    $[17] = props;
    $[18] = ref;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
});
DropdownMenuControlRow.displayName = "DropdownMenuControlRow";

/**
 * A 24px icon option inside a DropdownMenuControlRow, wrapped in a
 * DropdownMenuRadioGroup. Selecting it keeps the menu open.
 */
const DropdownMenuControlOption = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>>((t0, ref) => {
  const $ = _c(13);
  let className;
  let onSelect;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onSelect,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onSelect;
    $[3] = props;
  } else {
    className = $[1];
    onSelect = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex size-6 shrink-0 cursor-pointer select-none items-center justify-center rounded-md outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-600 data-[highlighted]:bg-neutral-50 data-[highlighted]:text-neutral-900 data-[state=checked]:bg-[#0000000f] data-[state=checked]:text-neutral-950 data-[state=checked]:data-[highlighted]:bg-black/10 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-neutral-400 dark:data-[highlighted]:bg-[#444545] dark:data-[highlighted]:text-white dark:data-[state=checked]:bg-white/6 dark:data-[state=checked]:text-white dark:data-[state=checked]:data-[highlighted]:bg-white/12 [&_svg]:size-3.5", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onSelect) {
    t2 = event => {
      onSelect?.(event);
      event.preventDefault();
    };
    $[6] = onSelect;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1 || $[11] !== t2) {
    t3 = <DropdownMenuPrimitive.RadioItem ref={ref} className={t1} onSelect={t2} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
});
DropdownMenuControlOption.displayName = "DropdownMenuControlOption";
const DropdownMenuShortcut = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("ml-auto text-xs tracking-widest opacity-60", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuCheckboxItemRight, DropdownMenuRadioItem, DropdownMenuRadioItemRight, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuControlRow, DropdownMenuControlOption, DropdownMenuShortcut, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup };